import React from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import DoughnutChart from './DoughnutChart';
import type { ChartDataLike } from '../../utils/chartHelpers';

export interface PlatformKeyMetric {
    label: string;
    val: string | number;
    icon: LucideIcon;
}

export interface PlatformOverviewTile {
    label: string;
    val: string | number;
    sub: string;
    desc: string;
    icon: LucideIcon;
    badge: string;
}

export interface PlatformGaugeBadge {
    label: string;
    val: string | number;
    dotClass?: string;
    textClass?: string;
}

export interface PlatformInsight {
    label: string;
    desc: string;
    tag: string;
    bgClass: string;
    textClass: string;
}

export interface AnalyticsOverviewCardProps {
    // Left rail — header card
    railTitle?: string;
    railIcon?: LucideIcon;
    railSelectorLabel?: string;
    railValue?: string | number;
    railUnit?: string;
    railSegmentsFilled?: number;
    railAxis?: [string, string, string];
    railStatusLabel?: string;
    railStatusText?: string;
    // Left rail — summary card
    summaryTitle?: string;
    summaryValue?: string | number;
    summaryUnit?: string;
    keyMetricsTitle?: string;
    keyMetrics?: PlatformKeyMetric[];
    // Right grid — overview tiles
    overviewTitle?: string;
    overviewAction?: React.ReactNode;
    tiles?: PlatformOverviewTile[];
    // Right grid — score gauge
    scoreTitle?: string;
    gaugeData?: ChartDataLike;
    gaugeOptions?: any;
    gaugeBadges?: PlatformGaugeBadge[];
    // Right grid — insights
    insightsTitle?: string;
    insights?: PlatformInsight[];
    // Right grid — CTA
    showCta?: boolean;
    ctaTitle?: React.ReactNode;
    ctaSubtitle?: string;
    ctaButtonLabel?: string;
    onCtaClick?: () => void;
}

const hasGaugeData = (data?: ChartDataLike): boolean =>
    Boolean(data?.datasets?.length && data.datasets.some((ds) => Array.isArray(ds.data) && ds.data.length > 0));

const AnalyticsOverviewCard: React.FC<AnalyticsOverviewCardProps> = ({
    railTitle,
    railIcon: RailIcon,
    railSelectorLabel,
    railValue,
    railUnit,
    railSegmentsFilled = 0,
    railAxis,
    railStatusLabel,
    railStatusText,
    summaryTitle,
    summaryValue,
    summaryUnit,
    keyMetricsTitle,
    keyMetrics = [],
    overviewTitle,
    overviewAction,
    tiles = [],
    scoreTitle,
    gaugeData,
    gaugeOptions,
    gaugeBadges = [],
    insightsTitle,
    insights = [],
    showCta = false,
    ctaTitle,
    ctaSubtitle,
    ctaButtonLabel,
    onCtaClick,
}) => {
    const showRailCard =
        railTitle ||
        RailIcon ||
        railSelectorLabel ||
        railValue != null ||
        railUnit ||
        railSegmentsFilled > 0 ||
        railAxis ||
        railStatusLabel ||
        railStatusText;

    const showSummaryCard =
        summaryTitle ||
        summaryValue != null ||
        summaryUnit ||
        keyMetricsTitle ||
        keyMetrics.length > 0;

    const showOverviewSection = overviewTitle || overviewAction || tiles.length > 0;

    const showGaugeSection =
        scoreTitle || hasGaugeData(gaugeData) || gaugeBadges.length > 0;

    const showInsightsSection = insightsTitle || insights.length > 0;

    return (
        <div className={`w-full flex flex-col xl:flex-row gap-theme overflow-hidden p-2 sm:p-4 lg:p-0`}>
            {(showRailCard || showSummaryCard) && (
            <div className={`w-full xl:w-72 flex flex-col gap-theme shrink-0`}>
                {showRailCard && (
                <div className={`bg-primary-600 rounded-theme p-theme text-white flex flex-col h-full shadow-lg hover:shadow-theme-hover hover:-translate-y-1 transition-all duration-300`}>
                    {(railTitle || RailIcon || railSelectorLabel) && (
                    <div className="flex items-center justify-between mb-6">
                        {(railTitle || RailIcon) && (
                        <div className="flex items-center gap-3">
                            {RailIcon ? (
                            <div className="w-10 h-10 rounded-2xl bg-white/10 flex items-center justify-center">
                                <RailIcon className="w-5 h-5 text-white fill-white" />
                            </div>
                            ) : null}
                            {railTitle ? <span className="text-lg font-bold">{railTitle}</span> : null}
                        </div>
                        )}
                        {railSelectorLabel ? (
                            <div className="flex items-center gap-1 px-3 py-1 bg-white/10 rounded-xl text-xxs font-bold border border-white/10">
                                {railSelectorLabel} <ChevronDown className="w-3 h-3" />
                            </div>
                        ) : null}
                    </div>
                    )}

                    {(railValue != null || railUnit || railSegmentsFilled > 0 || railAxis) && (
                    <div className="mb-8">
                        {(railValue != null || railUnit) && (
                        <div className="flex items-baseline gap-2 mb-2">
                            {railValue != null ? (
                                <span className="text-5xl font-black tracking-tighter">{railValue}</span>
                            ) : null}
                            {railUnit ? (
                                <span className="text-white/60 font-bold text-xs uppercase tracking-widest">{railUnit}</span>
                            ) : null}
                        </div>
                        )}
                        <div className="flex gap-1 mb-3">
                            {Array.from({ length: 12 }).map((_, i) => (
                                <div
                                    key={i}
                                    className={`flex-1 h-8 rounded-lg transition-all duration-500 ${i < railSegmentsFilled ? 'bg-white shadow-[0_0_15px_rgba(255,255,255,0.4)]' : 'bg-white/20'}`}
                                />
                            ))}
                        </div>
                        {railAxis && (
                        <div className="flex justify-between text-[9px] font-bold text-white/40 tracking-widest uppercase">
                            <span>{railAxis[0]}</span>
                            <span>{railAxis[1]}</span>
                            <span>{railAxis[2]}</span>
                        </div>
                        )}
                    </div>
                    )}

                    {(railStatusLabel || railStatusText) && (
                    <div className="mt-auto flex items-center justify-between pt-6 border-t border-white/10">
                        {railStatusLabel ? (
                            <span className="text-xs font-bold text-white/50 uppercase tracking-widest">{railStatusLabel}</span>
                        ) : <span />}
                        {railStatusText ? (
                        <div className="flex items-center gap-2 px-3 py-1.5 bg-white rounded-full text-xs font-black shadow-lg shadow-white/5 text-primary-600">
                            <div className="w-2 h-2 rounded-full bg-success" />
                            {railStatusText}
                        </div>
                        ) : null}
                    </div>
                    )}
                </div>
                )}

                {showSummaryCard && (
                <div className={`bg-white rounded-theme p-theme flex flex-col gap-theme shadow-sm border border-gray-100 hover:shadow-theme-hover hover:-translate-y-1 transition-all duration-300`}>
                    {(summaryTitle || summaryValue != null || summaryUnit) && (
                    <div>
                        {summaryTitle ? (
                            <p className="text-xxs font-black text-gray-400 uppercase tracking-widest mb-1">{summaryTitle}</p>
                        ) : null}
                        {(summaryValue != null || summaryUnit) && (
                        <div className="flex items-baseline gap-2">
                            {summaryValue != null ? (
                                <span className="text-3xl font-black text-gray-900 tracking-tighter">{summaryValue}</span>
                            ) : null}
                            {summaryUnit ? (
                                <span className="text-gray-400 font-bold text-xxs uppercase tracking-widest">{summaryUnit}</span>
                            ) : null}
                        </div>
                        )}
                    </div>
                    )}
                    {keyMetrics.length > 0 && (
                    <div className="space-y-4">
                        {keyMetricsTitle ? (
                            <p className="text-xxs font-black text-gray-400 uppercase tracking-widest border-b border-gray-50 pb-2">{keyMetricsTitle}</p>
                        ) : null}
                        {keyMetrics.map((metric) => (
                            <div key={metric.label} className="flex items-center justify-between group">
                                <div className="flex items-center gap-2.5">
                                    <div className="w-7 h-7 rounded-lg bg-gray-50 flex items-center justify-center text-gray-400 group-hover:bg-primary-50 group-hover:text-primary-600 transition-all">
                                        <metric.icon className="w-3.5 h-3.5" />
                                    </div>
                                    <span className="text-xs font-bold text-gray-800 group-hover:text-gray-900 transition-colors">{metric.label}</span>
                                </div>
                                <span className="text-sm font-black text-gray-900 tracking-tight">{metric.val}</span>
                            </div>
                        ))}
                    </div>
                    )}
                </div>
                )}
            </div>
            )}

            <div className={`flex-1 flex flex-col gap-theme`}>
                {showOverviewSection && (
                <div className={`bg-white rounded-theme p-theme shadow-sm border border-gray-100 flex flex-col gap-theme hover:shadow-theme-hover hover:-translate-y-1 transition-all duration-300`}>
                    {(overviewTitle || overviewAction) && (
                    <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
                        {overviewTitle ? <h2 className="dashboard-title">{overviewTitle}</h2> : null}
                        {overviewAction}
                    </div>
                    )}

                    {tiles.length > 0 && (
                    <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
                        {tiles.map((item) => (
                            <div key={item.label} className="bg-gray-50/50 rounded-3xl p-4 sm:p-6 border border-gray-100 hover:border-primary-100 transition-all group overflow-hidden">
                                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-3">
                                    <div className="flex items-center gap-2">
                                        <div className="w-5 h-5 rounded-full bg-white flex items-center justify-center shadow-sm text-primary-600 shrink-0">
                                            <item.icon className="w-3 h-3" />
                                        </div>
                                        <span className="text-[11px] sm:text-xs font-bold text-gray-900 truncate">{item.label}</span>
                                    </div>
                                    <div className="flex items-center gap-1.5 text-[9px] sm:text-xxs font-black text-primary-600/60 uppercase tracking-widest bg-white/50 px-2 py-0.5 rounded-full self-start sm:self-auto">
                                        <item.icon className="w-2.5 h-2.5" />
                                        {item.badge}
                                    </div>
                                </div>
                                <div className="mb-0">
                                    <p className="text-xs sm:text-sm text-gray-400 mb-2 line-clamp-1">{item.desc}</p>
                                    <div className="flex flex-col sm:flex-row sm:items-baseline gap-1 sm:gap-2">
                                        <span className="text-2xl sm:text-3xl font-black text-gray-900 tracking-tighter leading-none">{item.val}</span>
                                        <span className="text-gray-400 font-bold text-[9px] sm:text-xs uppercase tracking-widest whitespace-nowrap">{item.sub}</span>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                    )}
                </div>
                )}

                {(showGaugeSection || showInsightsSection || showCta) && (
                <div className={`flex-1 grid grid-cols-1 md:grid-cols-2 gap-theme`}>
                    {showGaugeSection && (
                    <div className={`bg-white rounded-theme p-theme shadow-sm border border-gray-100 flex flex-col hover:shadow-theme-hover hover:-translate-y-1 transition-all duration-300`}>
                        {scoreTitle && (
                        <div className="flex items-center justify-between mb-10">
                            <h2 className="dashboard-title">{scoreTitle}</h2>
                            <ChevronRight className="w-5 h-5 text-gray-300" />
                        </div>
                        )}

                        {hasGaugeData(gaugeData) && (
                        <div className="flex-1 flex flex-col items-center justify-center relative pb-6 sm:pb-10 overflow-hidden">
                            <div className="w-full h-40 sm:h-48 relative">
                                <DoughnutChart
                                    data={gaugeData!}
                                    options={gaugeOptions}
                                />
                            </div>
                        </div>
                        )}

                        {gaugeBadges.length > 0 && (
                        <div className="grid grid-cols-2 pt-8 border-t border-gray-50">
                            {gaugeBadges.map((badge) => (
                                <div key={badge.label} className="flex flex-col gap-1">
                                    <div className={`flex items-center gap-2 font-bold text-xxs uppercase tracking-widest ${badge.textClass ?? 'text-primary-600'}`}>
                                        <div className={`w-2 h-2 rounded-full ${badge.dotClass ?? 'bg-primary-600'}`} />
                                        {badge.label}
                                    </div>
                                    <span className="text-xl font-black text-gray-900 pl-4">{badge.val}</span>
                                </div>
                            ))}
                        </div>
                        )}
                    </div>
                    )}

                    {(showInsightsSection || showCta) && (
                    <div className={`flex flex-col gap-theme`}>
                        {showInsightsSection && (
                        <div className={`bg-white rounded-theme p-theme shadow-sm border border-gray-100 flex-1 hover:shadow-theme-hover hover:-translate-y-1 transition-all duration-300`}>
                            {insightsTitle && (
                            <div className="flex items-center justify-between mb-4">
                                <h3 className="dashboard-title">{insightsTitle}</h3>
                                <ChevronRight className="w-5 h-5 text-gray-300" />
                            </div>
                            )}
                            {insights.length > 0 && (
                            <div className="space-y-4">
                                {insights.map((item) => (
                                    <div key={item.label} className="p-5 rounded-3xl bg-gray-50/80 border border-gray-100 flex flex-col gap-2 group transition-all">
                                        <div className="flex items-center gap-2">
                                            <div className={`w-2 h-2 rounded-full ${item.bgClass}`} />
                                            <span className={`text-xxs font-black uppercase tracking-widest ${item.textClass}`}>{item.tag}</span>
                                        </div>
                                        <h4 className="text-sm font-black text-gray-900">{item.label}</h4>
                                        <p className="text-xxs font-bold text-gray-400">{item.desc}</p>
                                    </div>
                                ))}
                            </div>
                            )}
                        </div>
                        )}

                        {showCta && (ctaTitle || ctaSubtitle || ctaButtonLabel) && (
                            <div className={`bg-primary-600 rounded-theme p-theme text-white relative overflow-hidden shadow-xl hover:shadow-theme-hover hover:-translate-y-1 transition-all duration-300`}>
                                <div className="absolute -right-4 -bottom-4 w-40 h-40 bg-white/5 rounded-full blur-3xl" />
                                <div className="relative z-10 flex flex-col h-full justify-between gap-6">
                                    <div>
                                        {ctaTitle ? (
                                            <h3 className="text-lg font-black tracking-tight leading-tight mb-2">{ctaTitle}</h3>
                                        ) : null}
                                        {ctaSubtitle ? (
                                            <p className="text-white/50 text-xxs font-bold tracking-wider uppercase">{ctaSubtitle}</p>
                                        ) : null}
                                    </div>
                                    {ctaButtonLabel ? (
                                    <button type="button" onClick={onCtaClick} className="bg-white text-primary-600 font-black text-xs px-6 py-3 rounded-2xl flex items-center gap-2 self-start hover:shadow-2xl transition-all shadow-white/20">
                                        {ctaButtonLabel} <ChevronRight className="w-4 h-4" />
                                    </button>
                                    ) : null}
                                </div>
                            </div>
                        )}
                    </div>
                    )}
                </div>
                )}
            </div>
        </div>
    );
};

export default AnalyticsOverviewCard;
